import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Checkbox, SearchBar } from 'common/components';
import { User, WorkflowSummary } from 'types/accessManager.type';
import { UserRole } from 'types/userRoles';
import { useUpdateUserWorkflowAssignments } from 'services/hooks/mutations';

import styles from '../ManagerCommandCenter.module.scss';

interface AssignWorkflowsModalProps {
    isOpen: boolean;
    user: User;
    workflows: WorkflowSummary[];
    onClose: () => void;
    onSuccess: (message: string) => void;
    onError: (message: string) => void;
}

const AssignWorkflowsModal: React.FC<AssignWorkflowsModalProps> = ({
    isOpen,
    user,
    workflows,
    onClose,
    onSuccess,
    onError,
}) => {
    const initialIds = useMemo(
        () => new Set((user.workflow_assignments ?? []).map((wf) => wf.id)),
        [user.workflow_assignments]
    );

    const [selectedIds, setSelectedIds] = useState<Set<string>>(initialIds);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [search, setSearch] = useState('');

    const { updateUserWorkflowAssignments, isPending } = useUpdateUserWorkflowAssignments();

    useEffect(() => {
        setSelectedIds(initialIds);
        setConfirmingClear(false);
        setSearch('');
    }, [initialIds, isOpen]);

    const filteredWorkflows = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return workflows;
        return workflows.filter((wf) => wf.title.toLowerCase().includes(term));
    }, [workflows, search]);

    const isAgent = user.roles.includes(UserRole.AGENT);
    const hadAssignments = (user.workflow_assignments ?? []).length > 0;
    const isClearingAll = isAgent && hadAssignments && selectedIds.size === 0;

    const toggle = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
        if (confirmingClear) {
            setConfirmingClear(false);
        }
    };

    const submit = async () => {
        try {
            await updateUserWorkflowAssignments({
                id: user.id,
                workflow_ids: Array.from(selectedIds),
            });
            onSuccess(`Workflow assignments updated for ${user.first_name} ${user.last_name}.`);
        } catch (error: any) {
            const apiMessage = error?.response?.data?.message;
            const apiError = error?.response?.data?.error;
            const message = apiMessage || apiError || error?.message || 'Failed to update workflow assignments.';
            onError(message);
        }
    };

    const handleSave = () => {
        if (isClearingAll && !confirmingClear) {
            setConfirmingClear(true);
            return;
        }
        submit();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Workflow Assignments — ${user.first_name} ${user.last_name}`}
            size='md'
            showDefaultFooter={false}
        >
            {confirmingClear && (
                <div className={styles.modalConfirm}>
                    <strong>Heads up:</strong> Clearing all assignments for this agent means they will see no contacts
                    and won&apos;t receive any inbound calls. Click <strong>Confirm clear</strong> to proceed.
                </div>
            )}

            {workflows.length === 0 ? (
                <div className={styles.modalEmpty}>No workflows available in this tenant.</div>
            ) : (
                <>
                    <div className={styles.modalSearch}>
                        <SearchBar
                            placeholder='Search workflows...'
                            value={search}
                            onSearch={setSearch}
                            onChange={setSearch}
                            debounceDelay={0}
                        />
                    </div>
                    <div className={styles.modalList}>
                        {filteredWorkflows.length === 0 ? (
                            <div className={styles.modalEmpty}>No workflows match &quot;{search}&quot;.</div>
                        ) : (
                            filteredWorkflows.map((wf) => (
                                <div key={wf.id} className={styles.modalListItem}>
                                    <Checkbox
                                        label={wf.title}
                                        checked={selectedIds.has(wf.id)}
                                        onChange={() => toggle(wf.id)}
                                        disabled={isPending}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            <div className='flex justify-end gap-md mt-xl'>
                <Button variant='outline-secondary' onClick={onClose} disabled={isPending}>
                    Cancel
                </Button>
                <Button variant='primary' onClick={handleSave} disabled={isPending || workflows.length === 0}>
                    {isPending
                        ? 'Saving...'
                        : isClearingAll && confirmingClear
                          ? 'Confirm clear'
                          : isClearingAll
                            ? 'Clear all assignments'
                            : 'Save'}
                </Button>
            </div>
        </Modal>
    );
};

export default AssignWorkflowsModal;
